/**
 * Publisher caps — TanStack Query hooks (FS-822).
 *
 * Thin wrappers over `./api.ts` adding cache keys + invalidation. Used by
 * `PublisherCapsPage.tsx`. Raw `fetch()` is confined to `api.ts` per dash
 * contract rule D6.
 */

import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";

import {
	listCapsForAffiliate,
	publisherCapsQueryKeys,
	upsertCap,
	type PublisherCapView,
	type UpsertCapArgs,
} from "./api";

/**
 * usePublisherCapsList — fetches cap rows for a single affiliate. When the
 * affiliate id is blank the query is disabled so the operator table can
 * render the "enter affiliate" empty state without flapping a request.
 */
export function usePublisherCapsList(
	affiliateId: string,
): UseQueryResult<PublisherCapView[], Error> {
	const trimmed = affiliateId.trim();
	return useQuery({
		queryKey: publisherCapsQueryKeys.byAffiliate(trimmed),
		queryFn: () => listCapsForAffiliate(trimmed),
		enabled: trimmed.length > 0,
	});
}

/**
 * usePublisherCapsCreate — POST /publisher-caps upsert mutation. Invalidates
 * the per-affiliate cache on success so the table reloads with the new row.
 */
export function usePublisherCapsCreate(): UseMutationResult<
	PublisherCapView,
	Error,
	UpsertCapArgs
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (args: UpsertCapArgs) => upsertCap(args),
		onSuccess: (row) => {
			qc.invalidateQueries({
				queryKey: publisherCapsQueryKeys.byAffiliate(row.affiliate_id),
			});
			qc.invalidateQueries({ queryKey: publisherCapsQueryKeys.all });
		},
	});
}

/**
 * usePublisherCapsDelete — soft-deletes by flipping status to paused and
 * sending a POST upsert with the same (affiliate, offer, cap_type, window)
 * key. The backend treats same-key uniqueness as an atomic replace.
 */
export function usePublisherCapsDelete(): UseMutationResult<
	PublisherCapView,
	Error,
	PublisherCapView
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (row: PublisherCapView) =>
			upsertCap({
				affiliate_id: row.affiliate_id,
				offer_id: row.offer_id,
				cap_type: row.cap_type,
				limit: row.cap_limit,
				window: row.window_key,
				status: "paused",
			}),
		onSuccess: (row) => {
			qc.invalidateQueries({
				queryKey: publisherCapsQueryKeys.byAffiliate(row.affiliate_id),
			});
			qc.invalidateQueries({ queryKey: publisherCapsQueryKeys.all });
		},
	});
}
